import {
  ICustomField,
  PayeeTaxDocumentSharePermission
} from "@wingspanhq/payments/dist/interfaces";
import {
  PayeeRowsQueryFilter,
  PayerPayeeEngagementSearchableStatus
} from "../../../../../services/search";
import { customFieldToFiltersRequest } from "../../../../../utils/jsonFormSchema";
import {
  PayeeFilterEligibilityStatus,
  PayeeFilterRequirementStatus,
  PayeeFilters,
  PayeeFilterShareTaxDocument
} from "./types";

function mapShareTaxDocumentRequest(
  filtersValue?: PayeeFilterShareTaxDocument[]
) {
  const request: PayeeTaxDocumentSharePermission[] = [];

  (filtersValue || []).forEach(value => {
    switch (value) {
      case PayeeFilterShareTaxDocument.Shared:
        request.push(PayeeTaxDocumentSharePermission.Allow);
        break;
      case PayeeFilterShareTaxDocument.NotShared:
        request.push(
          PayeeTaxDocumentSharePermission.Unspecified,
          PayeeTaxDocumentSharePermission.Decline
        );
    }
  });

  return request;
}

function getSearchableStatusRequest(
  searchableStatus?: PayerPayeeEngagementSearchableStatus[],
  eligibilityStatus?: PayeeFilterEligibilityStatus
) {
  if (eligibilityStatus) {
    if (eligibilityStatus === PayeeFilterEligibilityStatus.Eligible) {
      return PayerPayeeEngagementSearchableStatus.Eligible;
    } else if (eligibilityStatus === PayeeFilterEligibilityStatus.NotEligible) {
      return {
        in: [
          PayerPayeeEngagementSearchableStatus.NotEligible,
          PayerPayeeEngagementSearchableStatus.NotSignedUp
        ]
      };
    }
  }

  if (searchableStatus) {
    return { in: searchableStatus };
  }

  return {
    "!=": PayerPayeeEngagementSearchableStatus.Inactive
  };
}

function getRequirementStatusRequest(
  filtersValue?: PayeeFilterRequirementStatus,
  requirementDefinitionId?: string
): PayeeRowsQueryFilter {
  const prefix = requirementDefinitionId;

  switch (filtersValue) {
    case PayeeFilterRequirementStatus.AwaitingContractorSignature:
      return {
        "requirements.isPendingPayeeSignature": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.AwaitingClientSignature:
      return {
        "requirements.isPendingPayerSignature": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.PendingBackgroundCheck:
      return {
        "requirements.isPendingBackgroundCheck": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.ProcessingBackgroundCheck:
      return {
        "requirements.isProcessingBackgroundCheck": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.FailedBackgroundCheck:
      return {
        "requirements.isFailedBackgroundCheck": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.Incomplete:
      return {
        "requirements.isIncomplete": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.Complete:
      return {
        "requirements.isComplete": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.PendingApproval:
      return {
        "requirements.isPendingReview": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.Rejected:
      return {
        "requirements.isRejectedReview": `${prefix}_true`
      };
    case PayeeFilterRequirementStatus.Approved:
      return {
        "requirements.isApprovedReview": `${prefix}_true`
      };
    default:
      return {};
  }
}

export const createListRequest = (
  filters: PayeeFilters,
  customFields?: ICustomField[]
) => {
  const requirementStatusFilter = getRequirementStatusRequest(
    filters.requirementStatus,
    filters.requirementDefinitionId
  );

  const filter: PayeeRowsQueryFilter = {
    "engagements.engagementId": filters.engagementId,
    searchableStatus: getSearchableStatusRequest(
      filters.searchableStatus,
      filters.eligibilityStatus
    ),
    "payerOwnedData.autoPayStrategy": { in: filters.autoPayStrategy },
    "payeeOwnedData.shareTaxDocument": {
      in: mapShareTaxDocumentRequest(filters.shareTaxDocument)
    },
    "labels.bulkBatchId": filters.bulkBatchId,
    payerId: { in: filters.payerId },
    searchableTinStatus: {
      in: filters.searchableTinStatus
    },
    "requirements.requirementDefinitionId": filters.requirementDefinitionId,
    ...requirementStatusFilter
  };

  // Custom fileds

  customFields?.forEach(customField => {
    const filterValue = filters[customField.key];
    if (
      typeof filterValue === "string" ||
      Array.isArray(filterValue) ||
      filterValue === undefined
    ) {
      filter[`payerOwnedData.customFields.${customField.key}`] =
        customFieldToFiltersRequest(customField, filterValue);
    }
  });

  return {
    filter
  };
};
